import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader, SectionHeader } from "../components/ui-blocks";
import { IMAGES } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Opportunities() {
  const { t } = useTranslation();
  const opportunities = t("data.opportunities", { returnObjects: true });
  const sectorOptions = t("opportunities.sectorOptions", { returnObjects: true });
  const investorOptions = t("opportunities.investorOptions", { returnObjects: true });

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    title: "",
    country: "",
    phone: "",
    sector_interest: "",
    investor_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.organization) {
      toast.error(t("opportunities.toast.missing"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/investment-brief`, form);
      toast.success(t("opportunities.toast.success"));
      setForm({
        full_name: "",
        email: "",
        organization: "",
        title: "",
        country: "",
        phone: "",
        sector_interest: "",
        investor_type: "",
        message: "",
      });
    } catch (err) {
      toast.error(t("opportunities.toast.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 focus:border-[#0B1A3D] rounded-full text-[#0B1A3D] placeholder:text-slate-400 px-5 py-3.5 outline-none transition-colors";
  const textareaCls =
    "w-full bg-white border border-slate-300 focus:border-[#0B1A3D] rounded-2xl text-[#0B1A3D] placeholder:text-slate-400 px-5 py-4 outline-none transition-colors";

  return (
    <>
      <PageHeader
        testId="opportunities-header"
        overline={t("opportunities.headerOverline")}
        title={t("opportunities.headerTitle")}
        lead={t("opportunities.headerLead")}
      />

      <section className="section-pad" data-testid="opportunities-grid">
        <div className="container-mtbl">
          <SectionHeader
            overline={t("opportunities.gridOverline")}
            title={t("opportunities.gridTitle")}
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {opportunities.map((opp, i) => (
              <div
                key={opp.title}
                data-testid={`opportunities-card-${i}`}
                className="group card-soft overflow-hidden"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={IMAGES[opp.img]}
                    alt={opp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7 md:p-8">
                  <p className="overline">
                    {t("opportunities.opportunityLabel")} · 0{i + 1}
                  </p>
                  <h3 className="mt-3 text-[#0B1A3D] text-xl md:text-2xl font-semibold tracking-tight">
                    {opp.title}
                  </h3>
                  <p className="mt-4 text-slate-600 leading-relaxed text-sm md:text-base">
                    {opp.summary}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p
            data-testid="opportunities-note"
            className="mt-12 border-l-2 border-slate-300 pl-5 text-slate-500 max-w-3xl leading-relaxed text-sm md:text-base"
          >
            {t("opportunities.confidentialNote")}
          </p>
        </div>
      </section>

      <section
        id="request"
        data-testid="opportunities-request"
        className="section-pad bg-paper border-t border-slate-200"
      >
        <div className="container-mtbl grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHeader
              overline={t("opportunities.formOverline")}
              title={t("opportunities.formTitle")}
              lead={t("opportunities.formLead")}
            />
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="brief-request-form"
            className="lg:col-span-7 space-y-5 bg-white border border-slate-200 rounded-2xl shadow-[0_10px_30px_rgba(11,26,61,0.06)] p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                placeholder={t("opportunities.form.fullName")}
                data-testid="brief-full-name"
                className={inputCls}
                required
              />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t("opportunities.form.email")}
                data-testid="brief-email"
                className={inputCls}
                required
              />
              <input
                name="organization"
                value={form.organization}
                onChange={handleChange}
                placeholder={t("opportunities.form.organization")}
                data-testid="brief-organization"
                className={inputCls}
                required
              />
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder={t("opportunities.form.title")}
                data-testid="brief-title"
                className={inputCls}
              />
              <input
                name="country"
                value={form.country}
                onChange={handleChange}
                placeholder={t("opportunities.form.country")}
                data-testid="brief-country"
                className={inputCls}
              />
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t("opportunities.form.phone")}
                data-testid="brief-phone"
                className={inputCls}
              />
              <select
                name="sector_interest"
                value={form.sector_interest}
                onChange={handleChange}
                data-testid="brief-sector"
                className={inputCls}
              >
                <option value="">{t("opportunities.form.sectorPlaceholder")}</option>
                {sectorOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                name="investor_type"
                value={form.investor_type}
                onChange={handleChange}
                data-testid="brief-investor-type"
                className={inputCls}
              >
                <option value="">{t("opportunities.form.investorPlaceholder")}</option>
                {investorOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={5}
              placeholder={t("opportunities.form.message")}
              data-testid="brief-message"
              className={textareaCls}
            />

            <button
              type="submit"
              disabled={submitting}
              data-testid="brief-submit"
              className="btn-primary w-full md:w-auto disabled:opacity-60"
            >
              {submitting
                ? t("opportunities.form.submitting")
                : t("opportunities.form.submit")}
            </button>
            <p className="text-xs text-slate-500 leading-relaxed pt-2">
              {t("opportunities.form.consent")}
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
